import { usePbrOptionsQuery } from '@/app/assets/components/upload/queries/usePbrOptionsQuery';
import { usePBRSettingsForm } from '@/app/assets/components/upload/hooks/useImportSettingsForm';
import { useCADOptionsQuery } from '../queries/useCADOptionsQuery';
import { PBROption, RPDXImportOption } from '@/app/assets/actions/importOptions';
import { useForm } from '@mantine/form';
import { useEffect } from 'react';

export type ModelImportConfig = { pbrConfig: Record<string, any>; CADConfig: Record<string, any> };

export const useImportOptions = (enableOptionsQuery: boolean) => {
  const {
    data: pbrOptions,
    isError: isErrorPbr,
    isLoading: isLoadingPbr,
  } = usePbrOptionsQuery({ enabled: enableOptionsQuery });
  const {
    data: CADOptions,
    isError: isErrorCad,
    isLoading: isLoadingCad,
  } = useCADOptionsQuery({ enabled: enableOptionsQuery });

  const transformModelImportConfig = (values: Record<string, any>, options: PBROption[]) => {
    return Object.entries(values).reduce((acc, [key, val]) => {
      const option = options?.find((o) => o.config.property === key);
      if (!option) {
        throw new Error(`PBR option with key ${key} not found`);
      }

      return { ...acc, [option.order + 1]: val };
    }, {});
  };

  const { form: pbrOptionsForm } = usePBRSettingsForm(pbrOptions ?? []);

  const CADOptionsForm = useForm();

  useEffect(() => {
    //Set initial values when options are fetched
    const values = CADOptionsForm.getValues();
    if (Object.keys(values).length === 0 && CADOptions) {
      CADOptionsForm.setValues(buildInitialValuesObject(CADOptions));
    }
  }, [CADOptions, CADOptionsForm]);

  return {
    pbrOptionsForm,
    CADOptionsForm,
    pbrOptions,
    CADOptions,
    transformModelImportConfig,
    isError: isErrorCad || isErrorPbr,
    isLoading: isLoadingCad || isLoadingPbr,
  };
};

type NestedObject = {
  [key: string]: string | NestedObject;
};

//Might not work for new options, especially if nested in array, but fingers crossed
function buildInitialValuesObject(options: RPDXImportOption[]): NestedObject {
  const result: NestedObject = {};

  for (const option of options) {
    const pathArray = option.schema_path.split('.');
    let current: NestedObject = result;

    for (let i = 0; i < pathArray.length - 1; i++) {
      const key = pathArray[i];
      if (!(key in current) || typeof current[key] !== 'object' || current[key] === null) {
        current[key] = {};
      }
      current = current[key] as NestedObject;
    }

    const lastKey = pathArray[pathArray.length - 1];
    current[lastKey] = option.option_subschema.default ?? undefined;
  }

  return result;
}
